import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components';
import { logError } from '../../../components/LogError';
import { appIsWorkingVar, isLoggedInVar } from '../../../graphql/cache';
import { updateAccessToken, updateRefreshToken, updateUserToken } from '../../../services/auth';
import { NavigationPath } from '../../../types/DomainTypes';
import AuthLayout from '../components/AuthLayout';
import OtpInput from '../components/OtpInput/OtpInput';
import ShowTextIn from '../components/ShowTextIn';
import { submitMfaChallenge } from '../helpers';
import { useAuth } from '../hooks/useAuthentication';
import useCountdown from '../hooks/useCountdown';
import useMfaChallengeMutation from '../hooks/useMfaChallengeMutation';
import { MfaRequestProps } from '../types';

export interface OTPFormProps {
  mfaToken: string | null;
  mfaOOBCode: string | null;
  emailAddress: string | null;
  timesStamp?: number;
  password?: string;
  passcode?: string;
  mfaCodeLength?: number;
  viewExpiryTimeInSeconds?: number;
  onBack?: () => void;
  onSucess?: () => void;
}

interface MFAViewState {
  isLoading: boolean;
  showWaitToSubmit: boolean;
  showCodeGenTimer: boolean;
  isViewExpired: boolean;
}

const FIVE_MINUTE = 10 * 60 * 1000;
const NOW_IN_MS = new Date().getTime();
const dateTimeAfter5Minutes = NOW_IN_MS + FIVE_MINUTE;

const defaultViewState = {
  isLoading: false,
  showWaitToSubmit: false,
  showCodeGenTimer: false,
  isViewExpired: false,
};
export const MFAChallengeForm = ({
  mfaToken,
  mfaOOBCode,
  emailAddress,
  password,
  passcode,
  timesStamp = NOW_IN_MS,
  mfaCodeLength = 6,
  viewExpiryTimeInSeconds = dateTimeAfter5Minutes,
  onBack,
  onSucess,
}: OTPFormProps): JSX.Element => {
  const [oneTimePasscode, setOneTimePasscode] = useState(passcode || '');
  const [error, setError] = useState<Error | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [viewState, setViewState] = useState<MFAViewState>({ ...defaultViewState });

  const [OTPInfo, setOTPInfo] = useState({ code: mfaOOBCode, token: mfaToken, email: emailAddress, password });
  const { submitOTP, loading } = useMfaChallengeMutation();
  const [isTimerEnabled, setIsTimerEnabled] = useState(false);
  const { data: countDownData, onTargetDateChange } = useCountdown(viewExpiryTimeInSeconds);
  const [, , minutes, seconds] = countDownData;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const errorHandler = (message: string) => {
    setIsLoggingIn(false);

    throw new Error(message);
  };

  const onMFARequested = ({ mfaOOBCode: _mfaOOBCode, mfaToken: _mfaToken }: MfaRequestProps): void => {
    setOTPInfo({ ...OTPInfo, code: _mfaOOBCode, token: _mfaToken });
  };

  const { onLogin } = useAuth({
    errorHandler,
    onError: (e: Error) => {
      setIsLoggingIn(false);
      setError(e);
    },
    onMFARequested,
  });

  const onClearTimer = () => {
    setViewState({ ...viewState, showCodeGenTimer: false });
  };

  if (isTimerEnabled && minutes + seconds <= 0 && !viewState.isViewExpired) {
    setIsLoggingIn(false);
    setViewState({ ...viewState, isViewExpired: true });
    setError(new Error(t('auth:otpForm.viewExpired')));
  }

  if (isTimerEnabled && minutes + seconds > 0 && viewState.isViewExpired) {
    setViewState({ ...viewState, isViewExpired: false });
    setError(null);
  }

  const reloadView = (initialTimeStamp = new Date().getTime(), update?: Partial<MFAViewState>) => {
    onTargetDateChange(initialTimeStamp + FIVE_MINUTE);
    setIsTimerEnabled(true);
    setViewState({ ...defaultViewState, ...(update || {}) });
    setError(null);
  };

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const { token: _token, code: _code, email: _email } = OTPInfo;
    if (!_token || !_code || !_email) {
      logError('Invalid data submitted for MFA');
      setError(t('auth:otpForm.invalidData'));
      return;
    }
    setIsLoggingIn(true);
    setViewState({ ...viewState, showWaitToSubmit: true });
    submitMfaChallenge(
      { mfaOOBCode: _code, oneTimePasscode, mfaToken: _token },
      {
        appIsWorkingVar,
        submitOTP,
        t,
        onError: (message) => {
          setIsLoggingIn(false);
          logError(`FAILED: ${message}`);
          setError(new Error(message));
        },
        onSuccess: ({ portalAuthMfaVerify: { accessToken, refreshToken, userToken } }) => {
          setIsLoggingIn(false);
          updateAccessToken(accessToken);
          updateRefreshToken(refreshToken);
          updateUserToken(userToken);
          onSucess?.();
          navigate(NavigationPath.PORTFOLIO);
        },
        isLoggedInVar,
      },
    );
  }

  const onResendCode = () => {
    if (emailAddress && password) {
      // setViewState({ ...viewState, showCodeGenTimer: true });
      reloadView(new Date().getTime(), { showCodeGenTimer: true });
      onLogin({ email: emailAddress, password });
    }
  };

  useEffect(() => {
    reloadView(timesStamp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesStamp]);

  const isSubmitDisabled =
    viewState.isViewExpired ||
    loading ||
    viewState.showWaitToSubmit ||
    viewState.showCodeGenTimer ||
    oneTimePasscode.length !== mfaCodeLength;

  return (
    <AuthLayout
      title={t('auth:otpForm.title')}
      error={{ body: error?.message || '', onClose: () => setError(null) }}
      subTitle={t('auth:otpForm.subTitle')}
      classNames={{
        title: 'w-full max-w-[420px] px-4',
        subTitle: 'w-full max-w-[420px] px-4',
      }}
    >
      <div className="w-full max-w-[420px] px-4 sm:px-0">
        {/* MFA Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl dark:shadow-slate-900/50 p-6 sm:p-8 mt-6 sm:mt-8 border border-gray-100 dark:border-slate-700/50">
          <form
            className="flex flex-col items-center"
            aria-label="one-time-passcode"
            noValidate
            onSubmit={handleSubmit}
          >
            {/* OTP Input */}
            <div className="w-full flex justify-center">
              <OtpInput
                value={oneTimePasscode}
                valueLength={mfaCodeLength}
                onChange={(value) => {
                  setOneTimePasscode(value.trim());
                }}
              />
            </div>

            {/* Timer Display */}
            {!viewState.isViewExpired && (
              <div className="mt-6 flex items-center justify-center gap-2 text-sm">
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-slate-700">
                  <svg
                    className="w-4 h-4 text-gray-500 dark:text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-gray-600 dark:text-gray-300 font-medium tabular-nums">
                    {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                  </span>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              isDisable={isSubmitDisabled}
              className={`w-full mt-6 h-12 text-base font-semibold rounded-xl transition-all duration-200 ${
                isSubmitDisabled
                  ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/30 dark:shadow-teal-500/30 hover:shadow-xl hover:shadow-orange/40 dark:hover:shadow-teal-500/40 hover:scale-[1.02] active:scale-[0.98]'
              }`}
              isProcessing={isLoggingIn}
              props={{
                name: 'mfa',
              }}
            >
              {t('common:submit')}
            </Button>
          </form>
        </div>

        {/* Action Links */}
        <div className="mt-6 flex flex-col items-center gap-3">
          {!viewState.isViewExpired && viewState.showCodeGenTimer && (
            <div className="flex cursor-pointer justify-center text-gray-500 dark:text-gray-400">
              <ShowTextIn translationKey="auth:otpForm.resendCode" initValue={10} onEnd={onClearTimer} />
            </div>
          )}

          {viewState.isViewExpired && (
            <Button
              isLink={true}
              onClick={() => {
                if (onBack) onBack();
              }}
              className="text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300 font-medium transition-colors"
            >
              {t('auth:otpForm.backToLogin')}
            </Button>
          )}

          {!viewState.isViewExpired && viewState.showWaitToSubmit && (
            <div className="flex cursor-pointer justify-center text-gray-500 dark:text-gray-400">
              <ShowTextIn
                translationKey={t('auth:otpForm.waitToResubmit')}
                initValue={10}
                onEnd={() => setViewState({ ...viewState, showWaitToSubmit: false })}
              />
            </div>
          )}

          {!viewState.isViewExpired && !viewState.showCodeGenTimer && !viewState.showWaitToSubmit && (
            <Button
              isLink={true}
              onClick={onResendCode}
              className="text-orange dark:text-teal-400 hover:text-orange/80 dark:hover:text-teal-300 font-medium transition-colors"
            >
              {t('auth:otpForm.regenerateCode')}
            </Button>
          )}
        </div>
      </div>
    </AuthLayout>
  );
};
